import { Assert } from "../../Assert";
import { PartialNullable, RadPromiseLike } from "../../Extensions";
import { PathHelper } from "../../Helpers/PathHelper";
import { StringHelper } from "../../Helpers/StringHelper";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyString } from "../../RadObject";
import { DetailsPage } from "../Pages/DetailsPage";
import { ComboBoxLookupItem } from "./ComboBoxLookupItem";
import { IPropertyEditorComboBox, PropertyEditorComboBox, PropertyEditorComboBoxPropertyValues } from "./PropertyEditorComboBox";
import { NAMESPACE } from "./_Namespace";

export type PropertyEditorComboBoxLookupItemPropertyValues =
    PropertyEditorComboBoxPropertyValues
    & {
        lookupType: string;
        orderBy: "description" | "order";
    };

export type PropertyEditorComboBoxLookupItemProperties = {
    [name in keyof PropertyEditorComboBoxLookupItemPropertyValues]: Property<PropertyEditorComboBoxLookupItemPropertyValues[name]>
};

export interface IPropertyEditorComboBoxLookupItem extends IPropertyEditorComboBox, PropertyEditorComboBoxLookupItemProperties
{
}

export class PropertyEditorComboBoxLookupItem extends PropertyEditorComboBox implements IPropertyEditorComboBoxLookupItem
{
    // #region Static

    public static override classNamespace = NAMESPACE + "PropertyEditorComboBoxLookupItem";
    public static override className = "PropertyEditorComboBoxLookupItem";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<PropertyEditorComboBoxLookupItemPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                createEditorControl: () => new ComboBoxLookupItem(),
                orderBy: "description",
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Properties

    public lookupType!: PropertyString;
    public orderBy!: Property<"description" | "order">;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<PropertyEditorComboBoxLookupItemPropertyValues>)
    {
        super(propertyValues);

        // Trigger Property Changes
        this.lookupType.triggerChange();
        this.orderBy.triggerChange();
    }

    // #endregion

    // #region Protected Methods

    protected _disableIfPathControl()
    {
        const
            page = this.page.get();

        if (!(page instanceof DetailsPage))
            return;

        const
            pageDataSource = page.dataSource.get(),
            dataSourceProperty = this.dataSourceProperty.get();

        if (!pageDataSource)
        {
            this.visible.set(true);
            return;
        }

        const
            url = page.url.get(),
            location = url ? PathHelper.getLocation(url) : null,
            pathParts = location?.applicationRelativeUrl ? StringHelper.trimEnd(location.applicationRelativeUrl, "/").split("/") : [],
            entityName = page.entityName.get();

        if (entityName !== "LookupItem" || dataSourceProperty !== "typeLookupItemId")
            return;

        if (!page.newRecord.get())
        {
            this.enabled.set(false);
            return;
        }

        if
            (
            pathParts.length === 4
            && StringHelper.equals(pathParts[0], "LookupItems", { ignoreCase: true })
            && StringHelper.equals(pathParts[2], "LookupItemsByType", { ignoreCase: true })
        )
        {
            pageDataSource.propertyValueByPathSet(dataSourceProperty, pathParts[1]);

            this.enabled.set(false);
        }
        else
            this.enabled.set(true);
    }
    protected _lookupTypeChanged(change: PropertyChange<string>)
    {
        const
            editorControl = this.editorControl.get();

        Assert.isInstanceOf(ComboBoxLookupItem, editorControl, "editorControl");
        editorControl.lookupType.set(change.currentValue);
    }
    protected _orderByChanged(change: PropertyChange<"description" | "order">)
    {
        const
            editorControl = this.editorControl.get();

        Assert.isInstanceOf(ComboBoxLookupItem, editorControl, "editorControl");
        editorControl.orderBy.set(change.currentValue);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: PropertyEditorComboBoxLookupItemPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.lookupType = new PropertyString(this, propertyValues.lookupType, this._lookupTypeChanged);
        this.orderBy = new Property<"description" | "order">(this, "System.String, System.Private.CoreLib", propertyValues.orderBy, this._orderByChanged);
    }

    public override loadDataAsync()
    {
        this._disableIfPathControl();

        return super.loadDataAsync();
    }

    // #endregion

    // #endregion
}
